import { queryKeys, useHttpClient, useActiveOrganizationId } from '@repo/api-client/react';
import {
  ClientSchema,
  ClientsListResponseSchema,
  paginated,
  type Client,
} from '@repo/api-client/dtos';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { z } from 'zod';
import { getJSON } from '../internal/http';

type QueryEnableOptions = { enabled?: boolean };

/**
 * List the active org's clients via cursor-paginated
 * `useInfiniteQuery`. Pages of 50 are concatenated into a single
 * `clients` array so the existing screen consumers — which never
 * paginated explicitly — keep working unchanged. The
 * `hasNextPage`, `fetchNextPage`, `isFetchingNextPage` fields
 * power the (tabs)/clients FlatList's `onEndReached` callback.
 */
export const CLIENTS_PAGE_SIZE = 50;

export function useClients(options: QueryEnableOptions = {}) {
  const http = useHttpClient();
  const orgId = useActiveOrganizationId();
  const enabled = options.enabled ?? true;

  const {
    error,
    isPending: loading,
    data,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: orgId ? queryKeys.clients.all(orgId) : ['clients', 'idle'],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const search = new URLSearchParams({ limit: String(CLIENTS_PAGE_SIZE) });
      if (pageParam) search.set('cursor', pageParam);
      return getJSON(
        http,
        `/orgs/${orgId}/clients?${search.toString()}`,
        ClientsListResponseSchema,
      );
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && !!orgId,
  });

  const clients = useMemo<Client[] | undefined>(
    () =>
      data
        ? (data.pages.flatMap((page) => page.items) as Client[])
        : undefined,
    [data],
  );

  return {
    clients,
    nextCursor: data?.pages.at(-1)?.nextCursor,
    loading,
    error,
    refetch,
    hasNextPage,
    fetchNextPage: () => fetchNextPage(),
    isFetchingNextPage,
  };
}

export function useClient(clientId: string, options: QueryEnableOptions = {}) {
  const http = useHttpClient();
  const orgId = useActiveOrganizationId();
  const enabled = options.enabled ?? true;

  const {
    error,
    isPending: loading,
    data: client,
    refetch,
  } = useQuery({
    queryKey:
      orgId && clientId
        ? queryKeys.clients.detail(orgId, clientId)
        : ['clients', 'idle'],
    queryFn: () =>
      getJSON(http, `/orgs/${orgId}/clients/${clientId}`, ClientSchema),
    enabled: enabled && !!orgId && !!clientId,
  });

  return { client: client as Client | undefined, loading, error, refetch };
}

/**
 * Server-side activity feed for a single client. The response shape is
 * still `{ items, nextCursor? }` like every other paginated list — the
 * v0.1 server stubs the items array as empty until the activity feature
 * lands.
 */
export function useClientActivities(
  clientId: string,
  options: QueryEnableOptions = {},
) {
  const http = useHttpClient();
  const orgId = useActiveOrganizationId();
  const enabled = options.enabled ?? true;

  const ActivitiesSchema = paginated(z.unknown());

  return useQuery({
    queryKey:
      orgId && clientId
        ? queryKeys.clients.activities(orgId, clientId)
        : ['clients', 'idle'],
    queryFn: async () => {
      if (!orgId || !clientId) return { items: [] };
      return getJSON(http, `/orgs/${orgId}/clients/${clientId}/activities`, ActivitiesSchema);
    },
    enabled: enabled && !!orgId && !!clientId,
  });
}
